// A patient's place in a service point's queue (docs/plans/phase1-triage.md §2.8, §5.5).
//
//   waiting ──call──▶ called ──start──▶ in_service ──finish──▶ done
//      │  ◀──return──   │                    │
//      │                └──────start─────────┤ (a patient can also be started straight from waiting)
//      └─ transfer / cancel (waiting, called, and transfer from in_service) ──▶ transferred_out / cancelled
//
// done, transferred_out and cancelled are final. A transfer ends this entry and the
// caller creates the next one in the same transaction. Returning (not answered) keeps
// the original arrival time, so the patient keeps their place.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const QUEUE_ENTRY_STATUSES = [
  'waiting',
  'called',
  'in_service',
  'done',
  'transferred_out',
  'cancelled',
] as const;
export type QueueEntryStatus = (typeof QUEUE_ENTRY_STATUSES)[number];

export const ACTIVE_QUEUE_STATUSES: readonly QueueEntryStatus[] = [
  'waiting',
  'called',
  'in_service',
];

export const QUEUE_ACTIONS = ['call', 'return', 'start', 'finish', 'transfer', 'cancel'] as const;
export type QueueAction = (typeof QUEUE_ACTIONS)[number];

export type QueueTransitionErrorCode = 'queue.not_allowed' | 'queue.reason_required';

export const QUEUE_TRANSITION_ERRORS: Record<QueueTransitionErrorCode, string> = {
  'queue.not_allowed': 'That step is not possible from where the patient is in the queue',
  'queue.reason_required': 'Removing a patient from a queue needs a reason',
};

const ALLOWED: Record<QueueAction, readonly QueueEntryStatus[]> = {
  // Calling again (the patient didn't come the first time) is allowed and counted.
  call: ['waiting', 'called'],
  return: ['called'],
  start: ['waiting', 'called'],
  finish: ['in_service'],
  transfer: ['waiting', 'called', 'in_service'],
  cancel: ['waiting', 'called'],
};

const RESULT: Record<QueueAction, QueueEntryStatus> = {
  call: 'called',
  return: 'waiting',
  start: 'in_service',
  finish: 'done',
  transfer: 'transferred_out',
  cancel: 'cancelled',
};

export function queueTransition(input: {
  readonly from: QueueEntryStatus;
  readonly action: QueueAction;
  /** Required to cancel. */
  readonly reason?: string | null | undefined;
}): Result<QueueEntryStatus, QueueTransitionErrorCode> {
  if (!ALLOWED[input.action].includes(input.from)) return err('queue.not_allowed');
  if (input.action === 'cancel' && (input.reason ?? '').trim() === '') {
    return err('queue.reason_required');
  }
  return ok(RESULT[input.action]);
}

export function isActiveQueueStatus(status: QueueEntryStatus): boolean {
  return ACTIVE_QUEUE_STATUSES.includes(status);
}

export interface QueueOrderEntry {
  readonly id: string;
  /** From the triage category; lower is seen first; null (not triaged) goes last. */
  readonly queueRank: number | null;
  readonly priorityFlags: readonly string[];
  readonly enteredAt: Date;
  /**
   * The visit is an emergency (casualty) visit: seen before others of the same rank
   * (docs/plans/phase2-casualty.md §1.1 item 5, C3; TODO(clinical-verify)).
   */
  readonly emergency?: boolean;
}

export interface QueueOrderSettings {
  /** Flagged patients (elderly, pregnant, …) go ahead of others in the same rank. Plan §13, P2. */
  readonly flaggedFirst: boolean;
  /**
   * Patients not yet triaged come first, by arrival: a casualty queue, where triage decides
   * how urgent everyone else is (casualty plan C4; TODO(clinical-verify)).
   */
  readonly untriagedFirst?: boolean;
}

function compareEntries(
  a: QueueOrderEntry,
  b: QueueOrderEntry,
  settings: QueueOrderSettings,
): number {
  const unranked = settings.untriagedFirst === true ? Number.NEGATIVE_INFINITY : Infinity;
  const rankA = a.queueRank ?? unranked;
  const rankB = b.queueRank ?? unranked;
  if (rankA !== rankB) return rankA < rankB ? -1 : 1;
  const emergencyA = a.emergency === true;
  const emergencyB = b.emergency === true;
  if (emergencyA !== emergencyB) return emergencyA ? -1 : 1;
  if (settings.flaggedFirst) {
    const flaggedA = a.priorityFlags.length > 0;
    const flaggedB = b.priorityFlags.length > 0;
    if (flaggedA !== flaggedB) return flaggedA ? -1 : 1;
  }
  const time = a.enteredAt.getTime() - b.enteredAt.getTime();
  if (time !== 0) return time;
  // The final tiebreak makes the order total, so it never changes between two loads.
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The order patients are seen in: rank (untriaged last, or first in a casualty queue), then
 * emergency visits, then flags (when the setting is on), then arrival, then ID. Deterministic: the same entries always give the same order (DESIGN.md §1).
 * Returns a new array.
 */
export function orderQueue<T extends QueueOrderEntry>(
  entries: readonly T[],
  settings: QueueOrderSettings,
): T[] {
  return [...entries].sort((a, b) => compareEntries(a, b, settings));
}

/** Whole minutes waited, never negative (a workstation clock may be slightly behind). */
export function waitingMinutes(enteredAt: Date, now: Date): number {
  const millis = now.getTime() - enteredAt.getTime();
  if (Number.isNaN(millis)) throw new RangeError('Not a valid instant');
  return Math.max(0, Math.floor(millis / 60_000));
}

/** A daily queue ticket as shown on the display: at least 3 digits, e.g. 42 → "042". */
export function formatTicket(value: number): string {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new RangeError('A ticket number is a whole number from 1');
  }
  return String(value).padStart(3, '0');
}
